import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useProductFeedback } from "hooks/useProductFeedback";
import { AIForMeTool } from "lib/models/aiforme-models";
import { CatalogicalService } from "lib/services";
import { FeedbackStats } from "lib/services/catalogical-models";
import { Dispatch, SetStateAction, useCallback, useState } from "react";

/**
 * Handler function for submitting user votes/ratings
 */
export type VoteHandler = (taxId: TaxonomyIdItem, vote: number) => Promise<boolean>;

export interface UseVoteSubmissionOptions {
  /** The taxonomy ID of the item being voted on */
  taxonomyId: string;
  /** Handler function to submit the vote */
  voteHandler: VoteHandler;
  /** Catalogical service client to fetch updated item data */
  client: CatalogicalService<AIForMeTool>;
  /** Previous rating value (user's existing vote), defaults to 0 */
  previousRating?: number;
  /** Item's feedback stats for initializing the hook */
  itemFeedback?: FeedbackStats;
  /** Optional callback for tracking vote events (e.g., UXM analytics) */
  onTrackVote?: (rating: number) => void;
}

export interface UseVoteSubmissionReturn {
  /** Current rating value */
  rating: number;
  /** Setter for rating (for external updates like review form) */
  setRating: Dispatch<SetStateAction<number>>;
  /** Whether a vote submission is in progress */
  isSubmittingVote: boolean;
  /** Error message if vote submission failed */
  errorMessage: string | undefined;
  /** Current item feedback stats (may be updated after successful vote) */
  itemFeedbackStats: FeedbackStats | undefined;
  /** Setter for item feedback stats (for external updates) */
  setItemFeedbackStats: Dispatch<SetStateAction<FeedbackStats | undefined>>;
  /** Whether feedback is currently loading */
  isLoadingFeedback: boolean;
  /** Function to post feedback (for review form) */
  doPostFeedback: ReturnType<typeof useProductFeedback>["doPostFeedback"];
  /** Handler to call when user submits a new rating */
  handleVote: (newRating: number) => void;
}

/**
 * Custom hook to manage vote submission logic for AI tools.
 * Encapsulates state management, error handling, and feedback updates.
 */
export const useVoteSubmission = ({
  taxonomyId,
  voteHandler,
  client,
  previousRating = 0,
  itemFeedback,
  onTrackVote,
}: UseVoteSubmissionOptions): UseVoteSubmissionReturn => {
  const taxId = new TaxonomyIdItem(taxonomyId);
  const [rating, setRating] = useState(previousRating);
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const {
    itemFeedbackStats,
    setItemFeedbackStats,
    isLoading: isLoadingFeedback,
    doPostFeedback,
  } = useProductFeedback({
    domain: "aiforme",
    defaultValues: {
      itemFeedbackStats: itemFeedback,
    },
  });

  const handleVote = useCallback(
    (newRating: number) => {
      // Call optional tracking callback
      onTrackVote?.(newRating);

      setIsSubmittingVote(true);
      const votePromise = voteHandler(taxId, newRating);

      if (votePromise && typeof votePromise.then === "function") {
        votePromise
          .then((ok: boolean) => {
            if (!ok) {
              console.error("Vote submission not ok");
              setErrorMessage(getString("aiForMe.failedVote"));
            } else {
              setRating(newRating);
              // Get the latest item data from the server
              client
                .getCatalogItemByTaxonomyId(taxId)
                .then((response) => {
                  if (response && response.item) {
                    // Update the feedback state with the latest data
                    setItemFeedbackStats(response.item.feedback);
                  }
                })
                .catch((error) => {
                  console.error("Error fetching updated item data:", error);
                });
            }
            setIsSubmittingVote(false);
          })
          .catch((error) => {
            setErrorMessage(getString("aiForMe.failedVote"));
            setIsSubmittingVote(false);
            console.error("Vote submission error:", error);
          });
      } else {
        setIsSubmittingVote(false);
        setRating(previousRating);
      }
    },
    [taxonomyId, voteHandler, client, previousRating, onTrackVote]
  );

  return {
    rating,
    setRating,
    isSubmittingVote,
    errorMessage,
    itemFeedbackStats,
    setItemFeedbackStats,
    isLoadingFeedback,
    doPostFeedback,
    handleVote,
  };
};
